class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.closeHandler = this.close.bind(this);
    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      const modal = this.querySelector('[data-rc-protection-modal]');
      if (modal && !modal.hidden) {
        this.closeProtectionModal();
        return;
      }
      this.close();
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.closeHandler);
    this.addEventListener('click', this.onCartUpsellAdd.bind(this));
    this.addEventListener('click', this.onProtectionInfoClick.bind(this));
    this.addEventListener('click', this.onCheckoutPlusClick.bind(this));
    this.addEventListener('click', this.onStandardCheckoutClick.bind(this));
    this.addEventListener('submit', this.onCartFormSubmit.bind(this));
    this.setHeaderCartIconAccessibility();
    this.removeCheckoutPlusLineItem();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.classList.add('animate', 'active');

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.closeProtectionModal();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  onCartUpsellAdd(event) {
    const button = event.target.closest('[data-rc-cart-upsell-add]');
    if (!button) return;

    const upsellItem = button.closest('.rc-cart-upsell__item');
    const variantSelect = upsellItem && upsellItem.querySelector('[data-rc-cart-upsell-select]');
    const variantId = variantSelect ? variantSelect.value : button.getAttribute('data-variant-id');
    if (!variantId || button.disabled) return;

    event.preventDefault();

    const label = button.querySelector('span');
    const originalLabel = label ? label.textContent : button.textContent;
    button.disabled = true;
    if (label) label.textContent = 'Adding';
    this.setLoading(true);

    const body = {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    };

    fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.description || 'Cart add failed');
        this.openAfterAdd(data);
      })
      .catch(() => {
        this.setLoading(false);
        button.disabled = false;
        if (label) label.textContent = originalLabel;
      });
  }

  onProtectionInfoClick(event) {
    if (event.target.closest('[data-rc-protection-info-open]')) {
      event.preventDefault();
      this.openProtectionModal();
      return;
    }

    if (event.target.closest('[data-rc-protection-info-close]')) {
      event.preventDefault();
      this.closeProtectionModal();
    }
  }

  openProtectionModal() {
    const modal = this.querySelector('[data-rc-protection-modal]');
    if (!modal) return;

    modal.hidden = false;
    modal.classList.add('is-open');
    const dialog = modal.querySelector('.rc-protection-modal__dialog');
    if (dialog) dialog.focus();
  }

  closeProtectionModal() {
    const modal = this.querySelector('[data-rc-protection-modal]');
    if (!modal) return;

    modal.classList.remove('is-open');
    modal.hidden = true;
  }

  removeCheckoutPlusLineItem() {
    const updates = this.getCheckoutPlusVariantUpdates();
    const lineItem = this.querySelector('[data-rc-checkout-plus-line-item]');

    if (!Object.keys(updates).length || (!lineItem && !this.dataset.rcProtectionCartVariantIds) || this.checkoutPlusCleanupRunning) return;

    this.checkoutPlusCleanupRunning = true;

    fetch(`${routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates }),
    })
      .then(() => this.refreshContents())
      .catch(() => null)
      .finally(() => {
        this.checkoutPlusCleanupRunning = false;
      });
  }

  onCheckoutPlusClick(event) {
    const button = event.target.closest('[data-rc-checkout-plus-submit]');
    if (!button || button.disabled) return;

    event.preventDefault();
    const variantId = button.getAttribute('data-variant-id');
    const feeCents = button.getAttribute('data-fee-cents');

    button.disabled = true;
    button.classList.add('is-loading');
    this.markCheckoutTransition();

    this.clearCartError();

    if (!variantId) {
      this.submitCartCheckout();
      return;
    }

    this.removeProtectionBeforeCheckout()
      .then(() => this.addProtectionAndCheckout(variantId, feeCents))
      .catch((error) => {
        button.disabled = false;
        button.classList.remove('is-loading');
        this.showCartError(error.message || 'Checkout+ could not be added. Please try again.');
      });
  }

  onStandardCheckoutClick(event) {
    const button = event.target.closest('[data-rc-checkout-standard-submit]');
    if (!button || button.disabled) return;

    event.preventDefault();
    const variantId = button.getAttribute('data-variant-id');

    if (!variantId) {
      this.submitCartCheckout();
      return;
    }

    button.disabled = true;
    button.classList.add('is-loading');

    this.removeProtectionBeforeCheckout().then(() => this.submitCartCheckout()).catch(() => this.submitCartCheckout());
  }

  onCartFormSubmit(event) {
    if (event.target?.id !== 'CartDrawer-Form') return;
    this.markCheckoutTransition();
  }

  addProtectionAndCheckout(variantId, feeCents = 0) {
    return fetch(`${routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: 1,
        properties: {
          _checkout_plus: 'Package protection and free returns',
          _checkout_plus_calculated_fee_cents: String(feeCents),
        },
      }),
    })
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data.description || data.message || 'Checkout+ add failed');
        window.location.href = '/checkout';
      });
  }

  getCheckoutPlusVariantUpdates() {
    const variantIds = `${this.dataset.rcProtectionCartVariantIds || ''}`
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

    return variantIds.reduce((updates, id) => {
      updates[id] = 0;
      return updates;
    }, {});
  }

  removeProtectionBeforeCheckout() {
    const updates = this.getCheckoutPlusVariantUpdates();
    if (!Object.keys(updates).length) return Promise.resolve();

    return fetch(`${routes.cart_update_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ updates }),
    }).then((response) => {
      if (!response.ok) throw new Error('Unable to update package protection.');
    });
  }

  clearCartError() {
    const errorContainer = this.querySelector('#CartDrawer-CartErrors');
    if (errorContainer) errorContainer.textContent = '';
  }

  showCartError(message) {
    const errorContainer = this.querySelector('#CartDrawer-CartErrors');
    if (!errorContainer) return;

    errorContainer.textContent = message;
  }

  submitCartCheckout() {
    const form = this.querySelector('#CartDrawer-Form');
    if (!form) {
      this.markCheckoutTransition();
      window.location.href = '/checkout';
      return;
    }

    this.markCheckoutTransition();
    const checkoutInput = document.createElement('input');
    checkoutInput.type = 'hidden';
    checkoutInput.name = 'checkout';
    checkoutInput.value = 'checkout';
    form.appendChild(checkoutInput);
    form.submit();
  }

  markCheckoutTransition() {
    if (this.checkoutTransitionStarted) return;
    this.checkoutTransitionStarted = true;
    this.classList.add('is-checkout-loading');
    document.documentElement.classList.add('rc-cart-checkout-loading');
    document.body.classList.add('rc-cart-checkout-loading');
    this.setLoading(true);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    this.renderSections(parsedState.sections);

    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.open();
  }

  prepareForAdd(triggeredBy) {
    this.classList.remove('is-empty');
    this.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    this.open(triggeredBy);
    this.setLoading(true);
  }

  markAddFailed(message) {
    this.setLoading(false);
    if (message) this.showCartError(message);
  }

  openAfterAdd(parsedState) {
    this.classList.remove('is-empty');
    this.productId = parsedState.id;
    if (!this.classList.contains('active')) this.open();
    this.setLoading(true);

    if (parsedState.sections) {
      this.renderSections(parsedState.sections);
      this.setLoading(false);
      return Promise.resolve();
    }

    const refreshToken = Symbol('cart-refresh');
    this.activeRefreshToken = refreshToken;
    return this.refreshContents()
      .catch(() => {
        if (parsedState.sections) this.renderSections(parsedState.sections);
      })
      .finally(() => {
        if (this.activeRefreshToken === refreshToken) this.setLoading(false);
      });
  }

  refreshContents() {
    const sectionIds = this.getSectionsToRender().map((section) => section.id).join(',');
    const url = `${routes.cart_url}?sections=${sectionIds}&sections_url=${encodeURIComponent(window.location.pathname)}`;
    if (this.refreshAbortController) this.refreshAbortController.abort();
    this.refreshAbortController = new AbortController();

    return fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: this.refreshAbortController.signal,
    })
      .then((response) => response.json())
      .then((sections) => this.renderSections(sections));
  }

  renderSections(sections) {
    if (!sections) return;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !sections[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
    });

    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.closeHandler);
    this.removeCheckoutPlusLineItem();
    window.RoyalSc20Countdown?.init();
  }

  setLoading(isLoading) {
    const inner = this.querySelector('.drawer__inner');
    if (!inner) return;

    inner.toggleAttribute('aria-busy', isLoading);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

window.RoyalSc20Countdown = window.RoyalSc20Countdown || {
  storageKey: 'royal-sc20-countdown-end',
  durationMs: 20 * 60 * 1000,
  timer: null,
  getEndTime() {
    const stored = Number(window.sessionStorage.getItem(this.storageKey));
    const now = Date.now();
    if (stored && stored > now) return stored;

    const next = now + this.durationMs;
    window.sessionStorage.setItem(this.storageKey, String(next));
    return next;
  },
  format(ms) {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  },
  init() {
    const nodes = Array.prototype.slice.call(document.querySelectorAll('[data-rc-sc20-countdown-time]'));
    if (!nodes.length) return;

    const endTime = this.getEndTime();
    const tick = () => {
      const remaining = endTime - Date.now();
      nodes.forEach((node) => {
        node.textContent = this.format(remaining);
      });
    };

    tick();
    window.clearInterval(this.timer);
    this.timer = window.setInterval(tick, 1000);
  },
};

document.addEventListener('DOMContentLoaded', () => {
  window.RoyalSc20Countdown.init();
});

window.addEventListener('pageshow', () => {
  document.documentElement.classList.remove('rc-cart-checkout-loading');
  document.body.classList.remove('rc-cart-checkout-loading');
  document.querySelectorAll('cart-drawer.is-checkout-loading').forEach((drawer) => {
    drawer.classList.remove('is-checkout-loading');
    drawer.checkoutTransitionStarted = false;
    drawer.setLoading?.(false);
  });
});
